/**
 * buildVersion — "which build am I, and is a newer one being served?"
 *
 * Why this exists. The installed PWA is a chrome-less window onto the same
 * origin, and our service worker caches NOTHING (see public/sw.js), so there is
 * no waiting-worker lifecycle to hang a "new version available" prompt on. HTML
 * is served `no-cache` and `/_next/static/` is content-hashed + immutable, so a
 * COLD START always lands on the current build with no help from us.
 *
 * What never updates is a session that is already running. Android app-switching
 * does not navigate, so a PWA left in the task switcher keeps executing the
 * JavaScript it loaded days ago — and because NEXT_PUBLIC_* is inlined at build
 * time, it also keeps that build's constants. That population is exactly the one
 * the 2026-08-14 WebSocket reconnect fix targets, i.e. the users who most need a
 * fix are the slowest to receive it. This module closes that gap.
 */

/**
 * BUILD_VERSION is inlined by `next build` from the environment. The Dockerfiles
 * set it from the same LVDR_IMAGE_TAG that names the published static dir, and
 * scripts/write-version.mjs writes public/version.json from the SAME variable in
 * the same build — see that file for why a second, independent source would make
 * the banner permanent.
 *
 * The literal member access (not a computed key) is required: Next replaces
 * `process.env.NEXT_PUBLIC_*` textually at build time and cannot see through a
 * variable.
 */
export const BUILD_VERSION = process.env.NEXT_PUBLIC_BUILD_SHA?.trim() || 'dev';

/** Where the served build stamps its version. Angie serves it `no-cache`. */
export const VERSION_URL = '/version.json';

/**
 * fetchServedVersion returns the version the origin is serving right now, or
 * null if it cannot be determined.
 *
 * `cache: 'no-store'` is belt-and-braces on top of Angie's `no-cache` header:
 * a check that can be answered from the HTTP cache would compare the running
 * build against itself and never fire.
 *
 * Every failure path returns null rather than throwing. This runs on a timer in
 * the background of a working app; an offline blip, a 404 on an older deploy, or
 * a captive-portal HTML response must be silently ignored, never surfaced.
 */
export async function fetchServedVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    // Built conditionally: tsconfig has exactOptionalPropertyTypes, under which
    // `signal: undefined` is NOT assignable to RequestInit's `AbortSignal | null`.
    const init: RequestInit = { cache: 'no-store' };
    if (signal) init.signal = signal;
    const res = await fetch(VERSION_URL, init);
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (typeof body !== 'object' || body === null) return null;
    const version = (body as { version?: unknown }).version;
    return typeof version === 'string' && version.trim() !== '' ? version.trim() : null;
  } catch {
    // Includes AbortError (component unmounted / superseded) and any non-JSON
    // body — e.g. a captive portal returning an HTML login page for every URL.
    return null;
  }
}

/**
 * isStale reports whether `served` is a DIFFERENT build from the one running.
 *
 * Deliberately an inequality, not an ordering: shas are not comparable, and a
 * ROLLBACK is exactly as important to surface as a roll-forward — a client left
 * on the build that was just rolled back is running code the operator has
 * decided is bad.
 *
 * Both `null`/empty served and the `dev` sentinel return false. `dev` means a
 * local build with no sha, where every reload would otherwise look like an
 * update; and an unknown served version is not evidence of anything.
 */
export function isStale(running: string, served: string | null): boolean {
  if (!served || served === 'dev') return false;
  if (!running || running === 'dev') return false;
  return running !== served;
}
